import mockUser from '../mock/mockUser';
import plaidService from './plaidService';

// Simulated API delay
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

class DataService {
  constructor() {
    this.useRealData = true; // Toggle to use real Plaid data vs mock data
  }

  // Get all user data
  async getUserData() {
    await delay(800);
    return mockUser;
  }

  // Get specific data sections
  async getAccounts() {
    if (this.useRealData) {
      try {
        console.log('🔍 DataService: Fetching accounts from Plaid...');
        const plaidData = await plaidService.getAllData();
        console.log('📊 DataService: Plaid response:', plaidData);
        
        if (plaidData.connected && plaidData.accounts && plaidData.accounts.length > 0) {
          console.log('✅ DataService: Using real Plaid accounts data');
          // Transform Plaid data to match our expected format
          return plaidData.accounts.map(account => {
            // Handle both raw Plaid data and already-transformed data
            let balance = 0;
            if (account.balance !== undefined) {
              // Already transformed by server
              balance = account.balance;
            } else if (account.balances) {
              // Raw Plaid data
              const balances = account.balances || {};
              balance = balances.current || balances.available || balances.limit || 0;
            }
            
            return {
              id: account.account_id || account.id,
              type: this.mapPlaidAccountType(account.type, account.subtype),
              institution: account.institution || (account.name ? account.name.split(' ')[0] : 'Unknown'),
              balance: Math.abs(balance || 0), // Ensure positive numbers for display
              name: account.name || 'Unknown Account',
              official_name: account.official_name || account.name || 'Unknown Account',
              account_id: account.account_id || account.id,
              // Add debug info
              rawType: `${account.type || 'unknown'}/${account.subtype || 'unknown'}`,
              rawBalances: account.balances,
              debug_balance_source: account.balance !== undefined ? 'pre-transformed' : 'raw-plaid'
            };
          });
        } else {
          console.log('⚠️ DataService: No Plaid accounts found, using mock data');
        }
      } catch (error) {
        console.log('❌ DataService: Error fetching Plaid accounts, using mock data:', error.message);
      }
    }
    
    // Fallback to mock data
    console.log('📁 DataService: Using mock accounts data');
    const data = await this.getUserData();
    return data.accounts;
  }

  async getLiabilities() {
    if (this.useRealData) {
      try {
        console.log('🔍 DataService: Fetching liabilities from Plaid...');
        const plaidData = await plaidService.getAllData();
        console.log('📊 DataService: Plaid response:', plaidData);
        
        if (plaidData.connected && plaidData.liabilities && plaidData.liabilities.length > 0) {
          console.log('✅ DataService: Using real Plaid liabilities data');
          // Transform Plaid liabilities to match our expected format
          return plaidData.liabilities.map(liability => {
            // Handle both raw Plaid data and already-transformed data
            let balance = 0;
            if (liability.balance !== undefined) {
              // Already transformed by server
              balance = liability.balance;
            } else if (liability.balances) {
              // Raw Plaid data
              balance = liability.balances.current || 0;
            }
            
            return {
              id: liability.account_id || liability.id,
              type: this.mapPlaidLiabilityType(liability.type, liability.subtype),
              institution: liability.institution || (liability.name ? liability.name.split(' ')[0] : 'Unknown'),
              balance: Math.abs(balance || 0), // Make positive for debt
              name: liability.name || 'Unknown Liability',
              account_id: liability.account_id || liability.id,
              monthly_payment: liability.monthly_payment || liability.minimum_payment_amount || 0,
              interest_rate: liability.interest_rate || liability.apr_percentage || 0
            };
          });
        } else {
          console.log('⚠️ DataService: No Plaid liabilities found, using mock data');
        }
      } catch (error) {
        console.log('❌ DataService: Error fetching Plaid liabilities, using mock data:', error.message);
      }
    }
    
    // Fallback to mock data
    console.log('📁 DataService: Using mock liabilities data');
    const data = await this.getUserData();
    return data.liabilities;
  }

  async getMonthlySpending() {
    const data = await this.getUserData();
    return data.monthly_spending;
  }

  async getProfile() {
    const data = await this.getUserData();
    // Include monthly_spending in the profile data since Income page expects it there
    return {
      ...data.profile,
      monthly_spending: data.monthly_spending
    };
  }

  async getProtection() {
    const data = await this.getUserData();
    return data.protection;
  }

  async getFactFinder() {
    const data = await this.getUserData();
    return data.factFinder;
  }

  async getPaystubs() {
    const data = await this.getUserData();
    return data.paystubs;
  }

  async getTransactions() {
    if (this.useRealData) {
      try {
        console.log('🔍 DataService: Fetching transactions from Plaid...');
        const plaidData = await plaidService.getAllData();
        console.log('📊 DataService: Plaid response:', plaidData);
        
        if (plaidData.connected && plaidData.transactions && plaidData.transactions.length > 0) {
          console.log('✅ DataService: Using real Plaid transactions data');
          // Transform Plaid transactions to match our expected format
          return plaidData.transactions.map(transaction => ({
            id: transaction.transaction_id,
            date: transaction.date,
            amount: transaction.amount || 0, // Keep Plaid's amount format (positive = outflow, negative = inflow)
            category: transaction.category && transaction.category.length > 0 ? transaction.category[0] : 'Other',
            account_id: transaction.account_id,
            merchant: transaction.merchant_name || transaction.name || 'Unknown',
            type: transaction.amount > 0 ? 'debit' : 'credit',
            name: transaction.name || 'Unknown Transaction'
          }));
        } else {
          console.log('⚠️ DataService: No Plaid transactions found, using mock data');
        }
      } catch (error) {
        console.log('❌ DataService: Error fetching Plaid transactions, using mock data:', error.message);
      }
    }
    
    // Fallback to mock data
    console.log('📁 DataService: Using mock transactions data');
    const data = await this.getUserData();
    return data.transactions;
  }

  // New data methods for centralized data
  async getNotifications() {
    const data = await this.getUserData();
    return data.notifications;
  }

  async getInsights() {
    const data = await this.getUserData();
    return data.insights;
  }

  async getZecherScore() {
    const data = await this.getUserData();
    return data.zecherScore;
  }

  async getAIChatHistory() {
    const data = await this.getUserData();
    return data.aiChatHistory;
  }

  // Calculate derived data
  async getNetWorth() {
    const accounts = await this.getAccounts();
    const liabilities = await this.getLiabilities();
    const totalAssets = accounts.reduce((sum, acc) => sum + acc.balance, 0);
    const totalLiabilities = liabilities.reduce((sum, liab) => sum + liab.balance, 0);
    return {
      total: totalAssets - totalLiabilities,
      assets: totalAssets,
      liabilities: totalLiabilities
    };
  }

  async getNetWorthHistory() {
    const data = await this.getUserData();
    return data.netWorthHistory;
  }

  async getSpendingBreakdown() {
    const spending = await this.getMonthlySpending();
    const categories = Object.entries(spending).map(([name, amount]) => ({
      name,
      amount,
    }));
    const total = categories.reduce((sum, c) => sum + c.amount, 0);
    return {
      categories: categories.map((c) => ({
        ...c,
        percentage: Math.round((c.amount / total) * 100),
      })),
      total
    };
  }

  // Refresh data (no-op for mock)
  async refreshData() {
    return this.getUserData();
  }

  // Helper to map Plaid account types to our types
  mapPlaidAccountType(type, subtype) {
    if (type === 'depository') {
      if (subtype === 'checking') return 'checking';
      if (subtype === 'savings') return 'savings';
      if (subtype === 'cd') return 'savings'; // Map CDs to savings
      if (subtype === 'money market') return 'savings'; // Map money market to savings
      if (subtype === 'hsa') return 'savings'; // Map HSA to savings
      if (subtype === 'cash management') return 'checking'; // Map cash management to checking
      return 'checking'; // Default depository to checking
    }
    if (type === 'investment') {
      if (subtype === 'ira' || subtype === '401k') return 'retirement';
      return 'brokerage';
    }
    if (type === 'loan') return 'loan';
    if (type === 'credit') return 'credit_card';
    return type;
  }

  // Helper to map Plaid liability types to our types
  mapPlaidLiabilityType(type, subtype) {
    if (type === 'credit') return 'credit_card';
    if (type === 'loan') {
      if (subtype === 'student') return 'student_loan';
      if (subtype === 'auto') return 'auto_loan';
      if (subtype === 'mortgage') return 'mortgage';
      return 'loan';
    }
    return type;
  }
}

// Create singleton instance
const dataService = new DataService();
export default dataService; 